import type { ReactElement } from 'react';
import { Pencil, SlidersHorizontal, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DataTableHeaderRow,
  DataTableTable,
  DataTableTbody,
  DataTableTd,
  DataTableTh,
  DataTableThead,
  DataTableTr,
} from '@/components/ui/data-table';

import type { FhirIntegrationViewModel } from '../integrationApi';

export function AdminFhirIntegrationsTable({
  rows,
  onManage,
  onEdit,
  onDelete,
  pendingDeleteId,
}: {
  readonly rows: readonly FhirIntegrationViewModel[];
  readonly onManage: (row: FhirIntegrationViewModel) => void;
  readonly onEdit: (row: FhirIntegrationViewModel) => void;
  readonly onDelete: (row: FhirIntegrationViewModel) => void;
  readonly pendingDeleteId: string | null;
}): ReactElement {
  const { t } = useTranslation();
  const col = 'routes.admin.fhirIntegrations.entries.table';

  return (
    <DataTableTable aria-label={t('routes.admin.fhirIntegrations.entries.heading')}>
      <DataTableThead className="sticky top-0 z-10 bg-card">
        <DataTableHeaderRow>
          <DataTableTh className="h-[40px] py-0">{t(`${col}.name`)}</DataTableTh>
          <DataTableTh className="h-[40px] py-0">{t(`${col}.serviceRootUrl`)}</DataTableTh>
          <DataTableTh className="h-[40px] py-0" widthClassName="w-[10rem]">
            {t(`${col}.criteria`)}
          </DataTableTh>
          <DataTableTh className="h-[40px] py-0" widthClassName="w-[9rem]">
            {t(`${col}.actions`)}
          </DataTableTh>
        </DataTableHeaderRow>
      </DataTableThead>
      <DataTableTbody>
        {rows.map((row) => (
          <DataTableTr key={row.id} className="even:bg-muted/55">
            <DataTableTd className="h-[40px] py-0 text-foreground/90" dataOffset>
              <span className="inline-flex items-center gap-2">
                {row.name}
                {row.isDefault && <Badge variant="default">{t(`${col}.defaultBadge`)}</Badge>}
                {!row.active && <Badge variant="outline">{t(`${col}.inactiveBadge`)}</Badge>}
                {row.skipAuthentication && <Badge variant="outline">{t(`${col}.noAuthBadge`)}</Badge>}
              </span>
            </DataTableTd>
            <DataTableTd className="h-[40px] py-0 font-mono text-xs text-muted-foreground" dataOffset={false}>
              {row.fhirServiceRootUrl}
            </DataTableTd>
            <DataTableTd className="h-[40px] py-0 text-muted-foreground" dataOffset={false}>
              {t(`routes.admin.fhirIntegrations.entries.criteria.${row.authorizationCriteria}`)}
            </DataTableTd>
            <DataTableTd className="h-[40px] py-0" dataOffset={false}>
              <div className="flex items-center gap-1">
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label={t(`${col}.manageAction`)}
                  title={t(`${col}.manageAction`)}
                  onClick={() => {
                    onManage(row);
                  }}
                >
                  <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label={t(`${col}.editAction`)}
                  title={t(`${col}.editAction`)}
                  onClick={() => {
                    onEdit(row);
                  }}
                >
                  <Pencil className="h-4 w-4" aria-hidden="true" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label={t(`${col}.deleteAction`)}
                  title={t(`${col}.deleteAction`)}
                  loading={pendingDeleteId === row.id}
                  onClick={() => {
                    onDelete(row);
                  }}
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </Button>
              </div>
            </DataTableTd>
          </DataTableTr>
        ))}
      </DataTableTbody>
    </DataTableTable>
  );
}
